/**
 * Assessment state management hook
 *
 * Persists assessment data in localStorage and provides
 * a simple API for the assessment flow.
 */

import { useState, useCallback } from "react";
import type { TechStackInput } from "../lib/techStacks";
import type { OverallScore } from "../lib/scoring";
import type { AutomationOpportunity } from "../lib/automationFinder";
import { runAssessment } from "../lib/assessmentEngine";
import { findAutomationOpportunities } from "../lib/automationFinder";

export interface AssessmentResult {
  id: string;
  input: TechStackInput;
  score: OverallScore;
  opportunities: AutomationOpportunity[];
  createdAt: string;
}

const STORAGE_KEY = "aireadyscore_assessment";

function generateId(): string {
  return `ars_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function loadFromStorage(): AssessmentResult | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as AssessmentResult;
  } catch {
    // Ignore storage errors
  }
  return null;
}

function saveToStorage(result: AssessmentResult): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(result));
  } catch {
    // Ignore storage errors
  }
}

export function useAssessment() {
  const [result, setResult] = useState<AssessmentResult | null>(
    loadFromStorage,
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const runNewAssessment = useCallback((input: TechStackInput) => {
    setIsProcessing(true);

    // Simulate a brief processing delay for UX
    setTimeout(() => {
      const score = runAssessment(input);
      const opportunities = findAutomationOpportunities(input);

      const newResult: AssessmentResult = {
        id: generateId(),
        input,
        score,
        opportunities,
        createdAt: new Date().toISOString(),
      };

      saveToStorage(newResult);
      setResult(newResult);
      setIsProcessing(false);
    }, 500);
  }, []);

  const clearAssessment = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setResult(null);
  }, []);

  return {
    result,
    isProcessing,
    runNewAssessment,
    clearAssessment,
  };
}
